import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Clock, 
  FileQuestion, 
  DollarSign, 
  Users,
  TrendingDown,
  TrendingUp,
  Bell,
  BellOff,
  Filter,
  RefreshCw,
  Palette,
  Droplets,
  Wrench
} from "lucide-react";
import { PeriodSelector, KPICard } from "@/components/UIComponents";
import { formatCurrency, formatPercent, MONTHS, CONFIG } from "@/lib/config";
import TARGETS_CONFIG, { getStatusColor } from "@/lib/targets";
import api from "@/lib/api";
import { SanteDataWidget } from "@/components/SanteDataWidget"; // 🩺 [Phase 96]

const ALERT_TYPES = {
  transaction_non_categorisee: {
    label: "Transaction non catégorisée",
    icon: FileQuestion,
    severity: "warning",
    color: "amber"
  },
  heures_manquantes: {
    label: "Heures non saisies",
    icon: Clock,
    severity: "warning",
    color: "amber"
  },
  kpi_critique: {
    label: "KPI en zone rouge",
    icon: TrendingDown,
    severity: "critical",
    color: "red"
  },
  kpi_attention: {
    label: "KPI en zone orange",
    icon: AlertTriangle,
    severity: "warning",
    color: "amber"
  },
  cash_non_reconcilie: {
    label: "Cash non réconcilié",
    icon: DollarSign,
    severity: "warning",
    color: "amber"
  },
  employe_inactif: {
    label: "Employé sans heures",
    icon: Users,
    severity: "info",
    color: "blue"
  },
  code_couleur_manquant: {
    label: "Codes couleur manquants",
    icon: Palette,
    severity: "warning",
    color: "amber"
  },
  code_couleur_critique: {
    label: "Codes couleur — Employés sur chantier!",
    icon: Palette,
    severity: "critical",
    color: "red"
  },
  nettoyage_pression: {
    label: "Nettoyage à pression à venir",
    icon: Droplets,
    severity: "warning",
    color: "amber"
  },
  service_call: {
    label: "Service call",
    icon: Wrench,
    severity: "info",
    color: "blue"
  },
  // 🆕 [Phase v13.60.3 — 2026-08-12] Conflit non-bloquant de location d'équipement (nacelle double-réservée)
  conflit_location: {
    label: "Conflit de location d'équipement",
    icon: Wrench,
    severity: "warning",
    color: "amber"
  }
};

export const Alertes = () => {
  const [year, setYear] = useState(2025);
  const [month, setMonth] = useState(1);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [dashboardData, setDashboardData] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    const saved = localStorage.getItem('financehub_dismissed_alerts');
    return saved ? JSON.parse(saved) : [];
  });

  const [targets, setTargets] = useState(TARGETS_CONFIG);

  useEffect(() => {
    axios.get(`${window.__API_URL__ || ""}/api/configuration/targets`)
      .then(r => r.data)
      .then(d => { if (d.targets) setTargets(d.targets); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem('financehub_dismissed_alerts', JSON.stringify(dismissedAlerts));
  }, [dismissedAlerts]);

  useEffect(() => {
    const checkAlerts = async () => {
      setLoading(true);
      const newAlerts = [];
      const monthKey = `${year}-${String(month).padStart(2, '0')}`;

      try {
        const [dashboard, transactions] = await Promise.all([
          api.getDashboard(year, month),
          api.getTransactions({ year, month })
        ]);
        setDashboardData(dashboard);

        // 1. Vérifier les transactions non catégorisées
        const uncategorized = transactions.filter(t => !t.categorie || t.categorie === "autres" || t.categorie === "");
        if (uncategorized.length > 0) {
          newAlerts.push({
            id: `uncategorized-${monthKey}`,
            type: "transaction_non_categorisee",
            title: `${uncategorized.length} transaction(s) non catégorisée(s)`,
            description: `Ces transactions n'ont pas de catégorie assignée et ne sont pas incluses dans l'analyse des dépenses.`,
            severity: "warning",
            count: uncategorized.length,
            action: "/transactions",
            actionLabel: "Voir les transactions"
          });
        }

        // 2. Vérifier les KPIs en zone rouge/orange
        const globalKpi = dashboard?.kpis?.GLOBAL || {};
        const marges = globalKpi.marges || {};

        // Marge brute
        if (marges.brute !== undefined) {
          const margeBruteConfig = targets.rentabilite?.marge_brute;
          if (margeBruteConfig) {
            const status = getStatusColor(marges.brute, margeBruteConfig);
            if (status === "red") {
              newAlerts.push({
                id: `marge-brute-${monthKey}`,
                type: "kpi_critique",
                title: `Marge brute critique: ${formatPercent(marges.brute)}`,
                description: `La marge brute est sous le seuil rouge (${margeBruteConfig.thresholds.orange}%). Action immédiate requise.`,
                severity: "critical",
                metric: "marge_brute",
                value: marges.brute,
                action: "/rentabilite",
                actionLabel: "Voir la rentabilité"
              });
            } else if (status === "orange") {
              newAlerts.push({
                id: `marge-brute-attention-${monthKey}`,
                type: "kpi_attention",
                title: `Marge brute en attention: ${formatPercent(marges.brute)}`,
                description: `La marge brute est entre ${margeBruteConfig.thresholds.orange}% et ${margeBruteConfig.thresholds.green}%. Surveillance recommandée.`,
                severity: "warning",
                metric: "marge_brute",
                value: marges.brute,
                action: "/rentabilite",
                actionLabel: "Voir la rentabilité"
              });
            }
          }
        }

        // Marge nette
        if (marges.nette !== undefined) {
          const margeNetteConfig = targets.rentabilite?.marge_nette_avant_impots;
          if (margeNetteConfig) {
            const status = getStatusColor(marges.nette, margeNetteConfig);
            if (status === "red") {
              newAlerts.push({
                id: `marge-nette-${monthKey}`,
                type: "kpi_critique",
                title: `Marge nette critique: ${formatPercent(marges.nette)}`,
                description: `La marge nette est sous le seuil acceptable. Revoir la structure de coûts.`,
                severity: "critical",
                metric: "marge_nette",
                value: marges.nette,
                action: "/rentabilite",
                actionLabel: "Voir la rentabilité"
              });
            }
          }
        }

        // 3. Vérifier les heures (si stockées localement)
        const hoursData = localStorage.getItem('financehub_hours');
        if (hoursData) {
          const hours = JSON.parse(hoursData);
          const currentMonthHours = hours[monthKey];
          if (!currentMonthHours || Object.keys(currentMonthHours).length === 0) {
            newAlerts.push({
              id: `hours-missing-${monthKey}`,
              type: "heures_manquantes",
              title: `Heures non saisies pour ${MONTHS[month]} ${year}`,
              description: `Aucune heure de travail n'a été entrée pour ce mois. Les calculs de productivité seront incomplets.`,
              severity: "warning",
              action: "/ventilation-paie",
              actionLabel: "Saisir les heures"
            });
          }
        } else {
          newAlerts.push({
            id: `hours-never-${monthKey}`,
            type: "heures_manquantes",
            title: `Configuration des heures requise`,
            description: `Vous n'avez jamais saisi d'heures de travail. Configurez vos employés et leurs heures.`,
            severity: "warning",
            action: "/ventilation-paie",
            actionLabel: "Configurer les heures"
          });
        }

        // 4. Vérifier % heures terrain
        if (hoursData) {
          const hours = JSON.parse(hoursData);
          const employees = JSON.parse(localStorage.getItem('financehub_employees') || '[]');
          const currentMonthHours = hours[monthKey] || {};
          
          const terrainEmployees = employees.filter(e => e.type === "terrain" && e.isActive);
          const adminEmployees = employees.filter(e => e.type === "admin" && e.isActive);
          
          const totalTerrain = terrainEmployees.reduce((sum, e) => sum + (currentMonthHours[e.id] || 0), 0);
          const totalAdmin = adminEmployees.reduce((sum, e) => sum + (currentMonthHours[e.id] || 0), 0);
          const total = totalTerrain + totalAdmin;
          
          if (total > 0) {
            const pctTerrain = (totalTerrain / total) * 100;
            const pctTerrainConfig = targets.production?.pct_heures_terrain;
            if (pctTerrainConfig) {
              const status = getStatusColor(pctTerrain, pctTerrainConfig);
              if (status === "red") {
                newAlerts.push({
                  id: `pct-terrain-${monthKey}`,
                  type: "kpi_critique",
                  title: `% Heures terrain critique: ${formatPercent(pctTerrain)}`,
                  description: `Trop d'heures admin par rapport au terrain. Cible: ${pctTerrainConfig.thresholds.green}%+`,
                  severity: "critical",
                  metric: "pct_heures_terrain",
                  value: pctTerrain,
                  action: "/ventilation-paie",
                  actionLabel: "Voir les heures"
                });
              }
            }
          }
        }

        setAlerts(newAlerts);
      } catch (error) {
        console.error("Error checking alerts:", error);
      }

      // ── CRM Alerts ──
      try {
        const crmRes = await axios.get(`${window.__API_URL__ || ""}/api/crm/alertes`);
        if (crmRes.status === 200) {
          const crmData = crmRes.data;
          setAlerts(prev => [...prev, ...(crmData.alertes || [])]);
        }
      } catch (e) {
        console.error("Error CRM alerts:", e);
      }

      // 🆕 [Phase v13.60.3 — 2026-08-12] Conflits location non-bloquants
      try {
        const conflitsRes = await axios.get(`${window.__API_URL__ || ""}/api/alertes/conflits-location?statut=active`);
        if (conflitsRes.status === 200 && Array.isArray(conflitsRes.data?.alertes)) {
          const mapped = conflitsRes.data.alertes.map(a => ({
            id: `conflit-location-${a.id}`,
            type: "conflit_location",
            title: `Conflit nacelle : ${a.nom_equipement}${a.numero_unite_equipement ? ' #' + a.numero_unite_equipement : ''} — ${a.client_nom || 'Client'}`,
            description: `Contrat signé du ${a.date_prise} au ${a.date_remise} · ${(a.conflits || []).length} conflit(s) · ${(a.alternatives_disponibles || []).length} alternative(s) libre(s).`,
            severity: "warning",
            action: "/crm/calendrier-equipements",
            actionLabel: "Voir dans Location"
          }));
          setAlerts(prev => [...prev, ...mapped]);
        }
      } catch (e) {
        // Silencieux : rôles terrain reçoivent 403.
      }

      setLoading(false);
    };

    checkAlerts();
  }, [year, month, targets]);

  const handleDismiss = (alertId) => {
    setDismissedAlerts([...dismissedAlerts, alertId]);
  };

  const handleRestore = (alertId) => {
    setDismissedAlerts(dismissedAlerts.filter(id => id !== alertId));
  };

  const handleClearDismissed = () => {
    setDismissedAlerts([]);
  };

  // Filtrer les alertes
  const activeAlerts = alerts.filter(a => !dismissedAlerts.includes(a.id));
  const filteredAlerts = filterSeverity === "all" 
    ? activeAlerts 
    : activeAlerts.filter(a => a.severity === filterSeverity);

  const criticalCount = activeAlerts.filter(a => a.severity === "critical").length;
  const warningCount = activeAlerts.filter(a => a.severity === "warning").length;
  const infoCount = activeAlerts.filter(a => a.severity === "info").length;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  return (
    <div data-testid="alertes-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Bell className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500" />
            Centre d&apos;Alertes
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Notifications et problèmes à résoudre - {MONTHS[month]} {year}</p>
        </div>
        <PeriodSelector year={year} setYear={setYear} month={month} setMonth={setMonth} />
      </div>

      {/* 🩺 [Phase 96 — 2026-07-09] Widget Santé Data — état des modules audit */}
      <SanteDataWidget />


      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in delay-100">
        <KPICard
          title="Alertes actives"
          value={activeAlerts.length}
          subtitle={dismissedAlerts.length > 0 ? `${dismissedAlerts.length} ignorée(s)` : "Aucune ignorée"}
          icon={Bell}
          color={activeAlerts.length === 0 ? "emerald" : "amber"}
        />
        <KPICard
          title="Critiques"
          value={criticalCount}
          subtitle="Action immédiate requise"
          icon={XCircle}
          color={criticalCount > 0 ? "red" : "emerald"}
        />
        <KPICard
          title="Avertissements"
          value={warningCount}
          subtitle="À surveiller"
          icon={AlertTriangle}
          color={warningCount > 0 ? "amber" : "emerald"}
        />
        <KPICard
          title="Informations"
          value={infoCount}
          subtitle="Pour votre attention"
          icon={CheckCircle}
          color="blue"
        />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 animate-fade-in delay-150">
        <Button
          variant={filterSeverity === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterSeverity("all")}
        >
          Toutes ({activeAlerts.length})
        </Button>
        <Button
          variant={filterSeverity === "critical" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterSeverity("critical")}
          className={filterSeverity === "critical" ? "bg-red-600" : ""}
        >
          <XCircle className="w-4 h-4 mr-1" />
          Critiques ({criticalCount})
        </Button>
        <Button
          variant={filterSeverity === "warning" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterSeverity("warning")}
          className={filterSeverity === "warning" ? "bg-amber-600" : ""}
        >
          <AlertTriangle className="w-4 h-4 mr-1" />
          <span className="hidden sm:inline">Avertissements</span>
          <span className="sm:hidden">Avert.</span>
          &nbsp;({warningCount})
        </Button>
        {dismissedAlerts.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearDismissed}
            className="ml-auto"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Restaurer ignorées ({dismissedAlerts.length})
          </Button>
        )}
      </div>

      {/* Liste des alertes */}
      <div className="space-y-4 animate-fade-in delay-200">
        {filteredAlerts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {filterSeverity === "all" ? "Aucune alerte active" : `Aucune alerte ${filterSeverity === "critical" ? "critique" : "d'avertissement"}`}
              </h3>
              <p className="text-slate-500">
                {activeAlerts.length === 0 
                  ? "Tout est en ordre! Continuez votre excellent travail." 
                  : "Modifiez le filtre pour voir d'autres alertes."}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredAlerts.map(alert => {
            const alertConfig = ALERT_TYPES[alert.type] || {};
            const AlertIcon = alertConfig.icon || AlertTriangle;
            
            return (
              <Card 
                key={alert.id} 
                className={`border-l-4 ${
                  alert.severity === "critical" 
                    ? "border-l-red-500 bg-red-50/30" 
                    : alert.severity === "warning"
                    ? "border-l-amber-500 bg-amber-50/30"
                    : "border-l-blue-500 bg-blue-50/30"
                }`}
              >
                <CardContent className="py-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${
                        alert.severity === "critical" 
                          ? "bg-red-100" 
                          : alert.severity === "warning"
                          ? "bg-amber-100"
                          : "bg-blue-100"
                      }`}>
                        <AlertIcon className={`w-5 h-5 ${
                          alert.severity === "critical" 
                            ? "text-red-600" 
                            : alert.severity === "warning"
                            ? "text-amber-600"
                            : "text-blue-600"
                        }`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-medium text-slate-900">{alert.title}</h3>
                          <Badge variant={alert.severity === "critical" ? "destructive" : "outline"} className="text-xs">
                            {alert.severity === "critical" ? "Critique" : alert.severity === "warning" ? "Attention" : "Info"}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600">{alert.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center flex-wrap gap-2 self-end sm:self-start sm:flex-shrink-0">
                      {alert.action && (
                        <Button size="sm" variant="outline" asChild>
                          <a href={alert.action}>{alert.actionLabel}</a>
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleDismiss(alert.id)}
                        title="Ignorer cette alerte"
                      >
                        <BellOff className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Alertes ignorées */}
      {dismissedAlerts.length > 0 && filterSeverity === "all" && (
        <Card className="animate-fade-in delay-300 opacity-60">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <BellOff className="w-4 h-4" />
              Alertes ignorées ({dismissedAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {dismissedAlerts.map(alertId => {
                const alert = alerts.find(a => a.id === alertId);
                if (!alert) return null;
                return (
                  <Badge 
                    key={alertId} 
                    variant="outline" 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleRestore(alertId)}
                  >
                    {alert.title} <RefreshCw className="w-3 h-3 ml-1" />
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Alertes;
